/**
 * User-profile timezone helpers for calendar-day bucketing and due-date display.
 * Due dates are stored as date-only (YYYY-MM-DD); "today" is the calendar day in the user's zone.
 */

type YmdParts = { year: number; month: number; day: number };

function parseYmdParts(value: string): YmdParts | null {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);

  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  const probe = new Date(Date.UTC(year, month - 1, day, 12, 0, 0, 0));
  if (
    probe.getUTCFullYear() !== year ||
    probe.getUTCMonth() !== month - 1 ||
    probe.getUTCDate() !== day
  ) {
    return null;
  }

  return { year, month, day };
}

function ymdToUtcMiddayMs(value: string): number {
  const parsed = parseYmdParts(value);
  if (!parsed) return Number.NaN;
  return Date.UTC(parsed.year, parsed.month - 1, parsed.day, 12, 0, 0, 0);
}

export function effectiveUserTimeZone(stored?: string | null): string {
  const t = (stored || '').trim();
  if (t) {
    try {
      Intl.DateTimeFormat(undefined, { timeZone: t });
      return t;
    } catch {
      /* invalid IANA — fall back */
    }
  }
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

/** Calendar YYYY-MM-DD in IANA time zone for an instant */
export function calendarYmdInZone(instant: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(instant);
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function wallPartsInZone(
  instant: Date,
  timeZone: string,
): { ymd: string; hour: number; minute: number } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(instant);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';
  const hourRaw = get('hour');
  return {
    ymd: `${get('year')}-${get('month')}-${get('day')}`,
    hour: hourRaw === '24' ? 0 : Number(hourRaw),
    minute: Number(get('minute')),
  };
}

/** UTC ms for a civil wall time in an IANA zone, or null in a DST gap. */
export function utcMsForWallTime(
  ymd: string,
  hour: number,
  minute: number,
  timeZone: string,
): number | null {
  const parsed = parseYmdParts(ymd);
  if (!parsed || !Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  const wantedAsUtc = Date.UTC(parsed.year, parsed.month - 1, parsed.day, hour, minute, 0);
  let probe = wantedAsUtc;
  for (let i = 0; i < 4; i += 1) {
    const shown = wallPartsInZone(new Date(probe), timeZone);
    const shownAsUtc = Date.UTC(
      Number(shown.ymd.slice(0, 4)),
      Number(shown.ymd.slice(5, 7)) - 1,
      Number(shown.ymd.slice(8, 10)),
      shown.hour,
      shown.minute,
      0,
    );
    const delta = wantedAsUtc - shownAsUtc;
    probe += delta;
    if (delta === 0) break;
  }
  const roundTrip = wallPartsInZone(new Date(probe), timeZone);
  if (roundTrip.ymd === ymd && roundTrip.hour === hour && roundTrip.minute === minute) {
    return probe;
  }
  return null;
}

/** Keep a real clock. Spring-forward gaps move to the next existing minute. */
export function normalizeWallTimeInZone(
  ymd: string,
  hour: number,
  minute: number,
  timeZone: string,
): { ymd: string; hour: number; minute: number } {
  if (utcMsForWallTime(ymd, hour, minute, timeZone) != null) {
    return { ymd, hour, minute };
  }
  const parsed = parseYmdParts(ymd);
  if (!parsed) return { ymd, hour, minute };
  const base = Date.UTC(parsed.year, parsed.month - 1, parsed.day, hour, minute, 0);
  for (let add = 1; add <= 180; add += 1) {
    const next = new Date(base + add * 60_000);
    const candidate = {
      ymd: `${next.getUTCFullYear()}-${pad2(next.getUTCMonth() + 1)}-${pad2(next.getUTCDate())}`,
      hour: next.getUTCHours(),
      minute: next.getUTCMinutes(),
    };
    if (utcMsForWallTime(candidate.ymd, candidate.hour, candidate.minute, timeZone) != null) {
      return candidate;
    }
  }
  return { ymd, hour, minute };
}

export function extractYmdFromDue(value?: string | null): string | null {
  if (!value) return null;
  const m = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  if (!m) return null;
  return parseYmdParts(m[1]) ? m[1] : null;
}

/**
 * Calendar-day difference: dueYmd minus todayYmd (positive = future).
 * Both arguments must be YYYY-MM-DD.
 */
export function ymdDayDiff(dueYmd: string, todayYmd: string): number {
  const a = ymdToUtcMiddayMs(dueYmd);
  const b = ymdToUtcMiddayMs(todayYmd);
  if (Number.isNaN(a) || Number.isNaN(b)) return Number.NaN;
  return Math.round((a - b) / 86400000);
}

export function formatYmdShortMonthDay(ymd: string): string {
  const parsed = parseYmdParts(ymd);
  if (!parsed) return ymd;
  const { month: mo, day: d } = parsed;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[mo - 1]} ${d}`;
}

export function dueBadgeFromYmd(dueYmd: string, todayYmd: string): string {
  const delta = ymdDayDiff(dueYmd, todayYmd);
  const short = formatYmdShortMonthDay(dueYmd);
  if (Number.isNaN(delta)) return `Due ${short}`;
  if (delta < 0) return `Overdue · ${short}`;
  if (delta === 0) return 'Due today';
  if (delta === 1) return 'Due tomorrow';
  return `Due in ${delta}d · ${short}`;
}

export function dueBadgeColorClassFromYmd(dueYmd: string, todayYmd: string): string {
  const delta = ymdDayDiff(dueYmd, todayYmd);
  if (Number.isNaN(delta)) return 'text-white/70';
  if (delta < 0) return 'text-red-400/90';
  if (delta === 0) return 'text-orange-400/90';
  return 'text-white/70';
}
